/**
 * Accepting an invite — `docs/rowted-admin-v2.md` §5.3.
 *
 * Three steps, in one direction, with no way back: **set a password**, **enrol
 * an authenticator**, **save the recovery codes**. Then in.
 *
 * **Every step is one-way because each one has already happened.** The link is
 * spent the moment the password is set — `POST /auth/accept-invite` consumes
 * the token and opens a session that can do exactly one thing — so a Back
 * button would offer to redo something the server will refuse. What the
 * screen owes instead is that nobody can get stranded: the recovery codes are
 * shown once and the step will not advance until they have been acknowledged.
 *
 * **The session in the middle is real but nearly powerless.** It carries the
 * cookie and the CSRF token, and the account is still `invited` —
 * `requirePlatformSession` only loads sessions whose user is `active`, so the
 * only route it reaches is the enrolment below. There is no window in which a
 * half-enrolled admin can read a customer.
 */
import { useCallback, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ApiError, PASSWORD_MIN_LENGTH } from '@rowted/shared'
import { adminRequest, setAdminCsrfToken } from '../../lib/admin-api.js'
import { Button, Card } from '../../components/primitives.js'
import { Field } from '../../components/fields.js'
import { IconCheck, IconCopy, IconSpinner, IconWarn } from '../../frame/icons.js'

interface Accepted {
  result: 'enrol_mfa'
  csrfToken: string
  name: string
  email: string
  secret: string
  otpauthUri: string
  qr: string
}

interface Enrolled { result: 'active'; recoveryCodes: string[] }

type Step =
  | { at: 'password' }
  | { at: 'enrol'; accepted: Accepted }
  | { at: 'codes'; codes: string[] }

export function AcceptInvite() {
  const { token = '' } = useParams()
  const navigate = useNavigate()
  const [step, setStep] = useState<Step>({ at: 'password' })
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const [password, setPassword] = useState('')
  const [again, setAgain] = useState('')
  const [code, setCode] = useState('')
  const [saved, setSaved] = useState(false)

  const setThePassword = useCallback(async () => {
    setError(null)
    if (password !== again) { setError('The two passwords are not the same.'); return }
    setBusy(true)
    try {
      const accepted = await adminRequest<Accepted>('/auth/accept-invite', {
        method: 'POST',
        body: { token, password },
      })
      /* The session this opened is what the next step authenticates with. */
      setAdminCsrfToken(accepted.csrfToken)
      setStep({ at: 'enrol', accepted })
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That link did not work.')
    } finally { setBusy(false) }
  }, [again, password, token])

  const enrol = useCallback(async () => {
    setError(null)
    setBusy(true)
    try {
      const done = await adminRequest<Enrolled>('/auth/enrol-mfa', {
        method: 'POST',
        body: { code: code.trim() },
      })
      setStep({ at: 'codes', codes: done.recoveryCodes })
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That code was not accepted.')
    } finally { setBusy(false) }
  }, [code])

  return (
    <main className="flex min-h-screen items-center justify-center bg-bg p-4">
      <div className="flex w-full max-w-[460px] flex-col gap-4">
        <header className="flex flex-col gap-1">
          <h1 className="m-0 text-title">
            {step.at === 'password' ? 'Set your password'
              : step.at === 'enrol' ? 'Enrol two-step'
                : 'Save your recovery codes'}
          </h1>
          <p className="m-0 text-cell leading-[1.45] text-text-muted">
            {step.at === 'password'
              ? 'Rowted Admin reaches every customer’s data, so this account needs a password '
                + 'nobody else has and an authenticator app.'
              : step.at === 'enrol'
                ? `Scan this with an authenticator app, then type the six digits it shows.`
                : 'These are the only way back in if you lose your authenticator.'}
          </p>
          <Steps at={step.at} />
        </header>

        {error && (
          <p role="alert" className="m-0 rounded-card bg-danger-bg px-3 py-2 text-cell
                                     text-danger">
            {error}
          </p>
        )}

        <Card className="p-4">
          {step.at === 'password' && (
            <div className="flex flex-col gap-3.5">
              <Field
                label="Password"
                value={password}
                onChange={setPassword}
                type="password"
                autoFocus
                required
                hint={`At least ${PASSWORD_MIN_LENGTH} characters. A passphrase of four words
                      beats anything you would have to write down.`}
              />
              <Field
                label="Password again"
                value={again}
                onChange={setAgain}
                type="password"
                required
              />
              <Button
                variant="primary"
                onClick={() => void setThePassword()}
                disabled={busy || password.length < PASSWORD_MIN_LENGTH || again === ''}
              >
                {busy ? <IconSpinner size={15} /> : null}
                Continue
              </Button>
            </div>
          )}

          {step.at === 'enrol' && (
            <div className="flex flex-col gap-3.5">
              <p className="m-0 text-cell text-text-body">
                Signing in as <b>{step.accepted.email}</b>.
              </p>

              <div className="flex flex-col items-center gap-2 rounded-field bg-surface-alt p-4">
                <img
                  src={step.accepted.qr}
                  alt="The QR code for your authenticator app"
                  width={168}
                  height={168}
                  className="rounded-field bg-surface"
                />
                {/*
                  * The secret in words as well as a picture, because a person
                  * enrolling on the same device they are reading this on
                  * cannot point a camera at their own screen.
                  */}
                <Copyable label="Or type this key" value={step.accepted.secret} />
              </div>

              <Field
                label="The six digits"
                value={code}
                onChange={(next) => setCode(next.replace(/[^0-9]/g, '').slice(0, 6))}
                autoFocus
                required
                hint="From the app, not from the email."
              />
              <Button
                variant="primary"
                onClick={() => void enrol()}
                disabled={busy || code.length < 6}
              >
                {busy ? <IconSpinner size={15} /> : null}
                Enrol and continue
              </Button>
            </div>
          )}

          {step.at === 'codes' && (
            <div className="flex flex-col gap-3.5">
              <p className="m-0 flex items-start gap-1.5 text-cell leading-[1.45] text-warn">
                <span className="mt-px shrink-0"><IconWarn size={14} /></span>
                <span>
                  This is the only time these are shown. Each one works once. Put them
                  somewhere that is not the device with your authenticator on it.
                </span>
              </p>

              <ul className="m-0 grid list-none grid-cols-2 gap-1.5 rounded-field bg-surface-alt
                             p-3">
                {step.codes.map((one) => (
                  <li key={one} className="font-mono text-[13px] tracking-[0.04em] text-text">
                    {one}
                  </li>
                ))}
              </ul>

              <div className="flex items-center gap-2">
                <Copyable label="Copy all" value={step.codes.join('\n')} />
                <label className="ml-auto flex items-center gap-2 text-cell text-text">
                  <input
                    type="checkbox"
                    checked={saved}
                    onChange={(event) => setSaved(event.target.checked)}
                    className="h-4 w-4 accent-accent"
                  />
                  I’ve saved these
                </label>
              </div>

              <Button
                variant="primary"
                onClick={() => navigate('/admin/clients', { replace: true })}
                disabled={!saved}
              >
                <IconCheck size={15} />
                Open Rowted Admin
              </Button>
            </div>
          )}
        </Card>
      </div>
    </main>
  )
}

/** Where you are of three. Numbers, because the steps have no way back. */
function Steps({ at }: { at: Step['at'] }) {
  const order: Step['at'][] = ['password', 'enrol', 'codes']
  const here = order.indexOf(at)
  return (
    <ol className="m-0 mt-1 flex list-none gap-1.5 p-0">
      {order.map((one, index) => (
        <li
          key={one}
          className={`h-1 flex-1 rounded-full ${index <= here ? 'bg-accent' : 'bg-line'}`}
          aria-current={index === here ? 'step' : undefined}
        />
      ))}
    </ol>
  )
}

function Copyable({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <Button
      onClick={() => {
        void navigator.clipboard?.writeText(value).catch(() => {})
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
      }}
    >
      {copied ? <IconCheck size={15} /> : <IconCopy size={15} />}
      {copied ? 'Copied' : label}
    </Button>
  )
}
